import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Flame, Send, CalendarRange, RefreshCw, X, Activity } from 'lucide-react';
import { adminApi } from './adminApi';

const ICONS = {
  broadcast_sent: Send,
  digest_sent_scheduled: CalendarRange,
  digest_sent_manually: CalendarRange,
  contact_status_changed: Activity,
  contacts_bulk_status_changed: Activity,
  test_email_sent: Send,
  default: Activity,
};

const POLL_MS = 60_000; // refresh once a minute

const NotificationsBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ events: [], hot_leads: [], unread: 0 });
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);

  const refresh = async () => {
    try {
      const res = await adminApi.notifications();
      setData(res);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = async () => {
    setOpen((o) => !o);
    if (!open && data.unread > 0) {
      // Mark read after open animation; refresh state
      try {
        await adminApi.markNotificationsRead();
        setData((d) => ({ ...d, unread: 0 }));
      } catch (err) {
        // Non-critical: the next `load()` poll will re-sync the unread count.
        // Logged so we still see it in DevTools without breaking the UI.
        console.warn('NotificationsBell: mark-read failed', err?.response?.status || err?.message);
      }
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleOpen}
        data-testid="notifications-bell"
        className="relative w-10 h-10 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-white/70 hover:text-white transition flex items-center justify-center"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {data.unread > 0 && (
          <span
            data-testid="notifications-unread-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#83BF42] text-[#0F0F11] text-[10px] font-bold font-['Inter'] flex items-center justify-center"
          >
            {data.unread > 9 ? '9+' : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 mt-2 w-[360px] max-h-[520px] bg-[#15151A] border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-50 flex flex-col"
        >
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-white text-sm font-medium font-['Inter']">Notifications</h3>
            <div className="flex gap-1">
              <button
                onClick={refresh}
                data-testid="notifications-refresh"
                className="text-white/40 hover:text-white p-1"
                aria-label="Refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="text-white/40 hover:text-white p-1"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {/* Hot leads section */}
            {data.hot_leads.length > 0 && (
              <div className="px-3 py-2 border-b border-white/5">
                <p className="text-[10px] uppercase tracking-wider font-['Inter'] text-red-300 px-2 py-2 flex items-center gap-1">
                  <Flame className="w-3 h-3" /> Hot leads (24h)
                </p>
                {data.hot_leads.map((h) => (
                  <button
                    key={h.id}
                    onClick={() => { navigate('/admin/contacts'); setOpen(false); }}
                    data-testid={`notification-hot-${h.id}`}
                    className="w-full text-left px-2 py-2.5 rounded hover:bg-white/[0.03] flex items-start gap-2"
                  >
                    <Flame className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-['Inter'] truncate">
                        {h.firstName} {h.lastName} <span className="text-white/40">· {h.company}</span>
                      </p>
                      <p className="text-white/50 text-xs font-['Lato']">
                        Score {h.score}/100 · {h.created_at ? new Date(h.created_at).toLocaleString() : ''}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Recent activity */}
            <div className="px-3 py-2">
              <p className="text-[10px] uppercase tracking-wider font-['Inter'] text-white/40 px-2 py-2">
                Recent activity
              </p>
              {data.events.length === 0 ? (
                <p className="text-white/30 text-xs font-['Lato'] px-2 py-3 text-center">
                  All quiet for now.
                </p>
              ) : (
                data.events.slice(0, 8).map((e) => {
                  const Icon = ICONS[e.action] || ICONS.default;
                  const label = e.action.replace(/_/g, ' ');
                  return (
                    <div
                      key={e.id}
                      data-testid={`notification-event-${e.id}`}
                      className="px-2 py-2.5 rounded hover:bg-white/[0.03] flex items-start gap-2"
                    >
                      <Icon className="w-3.5 h-3.5 text-[#83BF42] mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm font-['Inter'] truncate">
                          {label}
                          {e.meta?.subject && (
                            <span className="text-white/50"> · {e.meta.subject}</span>
                          )}
                          {e.meta?.name && (
                            <span className="text-white/50"> · {e.meta.name}</span>
                          )}
                        </p>
                        <p className="text-white/40 text-xs font-['Lato']">
                          {e.actor_email} · {e.created_at ? new Date(e.created_at).toLocaleString() : ''}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="px-5 py-3 border-t border-white/5 bg-white/[0.02]">
            <Link
              to="/admin/activity"
              onClick={() => setOpen(false)}
              data-testid="notifications-view-all"
              className="text-[#83BF42] text-xs font-['Inter'] hover:underline"
            >
              View full activity log →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
